import { DEFAULT_API, apiCall, type SyncConfig } from './sync';
import type { Branding } from './plan';

/**
 * Anmeldung (docs/15): Die Leitung schaltet sie in den Einstellungen ein und
 * teilt den Link; Teilnehmer melden sich ohne Konto an. Läuft über die
 * Sync-API — braucht also Cloud-Sync für das Turnier.
 */
/**
 * Zusatzfrage im Formular (docs/15 §6): Kästchen, Zahl oder Freitext, wahlweise
 * Pflicht. Die Grundfelder sind fest, diese hängt die Leitung selbst an —
 * Harald: „Einmal melden sich die Teilnehmer zum Essen an, ein anderes Mal
 * zur Übernachtung."
 */
export interface RegistrationField {
  id: string;
  label: string;
  type: 'bool' | 'number' | 'text';
  required?: boolean;
}
export type RegistrationAnswers = Record<string, string | number | boolean>;
export const MAX_FIELDS = 10;

export function newFieldId(): string {
  return Math.random().toString(36).slice(2, 8);
}

export interface RegistrationConfig {
  regKey: string;
  open: boolean;
  deadline: number;
  title: string;
  date?: string;
  location?: string;
  note?: string;
  teamSize: number;
  maxTeams?: number;
  fields?: RegistrationField[];
  updatedAt: number;
}

export interface RegistrationPlayer {
  firstName: string;
  lastName: string;
}

export interface RegistrationInput {
  teamName?: string;
  players: RegistrationPlayer[];
  club?: string;
  email?: string;
  phone?: string;
  note?: string;
  answers?: RegistrationAnswers;
}

export interface AdminRegistration extends RegistrationInput {
  id: string;
  createdAt: number;
  position: number;
}

export interface PublicRegistration {
  id: string;
  teamName?: string;
  players: RegistrationPlayer[];
  club?: string;
  position: number;
  waitlist: boolean;
}

export interface RegistrationInfo {
  title: string;
  date?: string;
  location?: string;
  note?: string;
  teamSize: number;
  maxTeams?: number;
  fields?: RegistrationField[];
  deadline: number;
  open: boolean;
  accepting: boolean;
  count: number;
  registrations: PublicRegistration[];
  /** Logo und Farbe des Veranstalters — nur, wenn dessen Stufe `branding` hat (docs/21) */
  branding?: Branding;
}

export type RegistrationBody = Omit<RegistrationConfig, 'regKey' | 'updatedAt'>;

// --- Leitung -----------------------------------------------------------------

export async function setRegistration(cfg: SyncConfig, body: RegistrationBody): Promise<RegistrationConfig> {
  return apiCall<RegistrationConfig>(cfg.apiUrl, cfg.key, 'PUT', `/t/${encodeURIComponent(cfg.tournamentId)}/reg`, body);
}

export async function adminRegistrations(cfg: SyncConfig): Promise<{ config: RegistrationConfig | null; registrations: AdminRegistration[] }> {
  return apiCall(cfg.apiUrl, cfg.key, 'GET', `/t/${encodeURIComponent(cfg.tournamentId)}/reg`);
}

export async function adminDeleteRegistration(cfg: SyncConfig, rid: string): Promise<void> {
  await apiCall(cfg.apiUrl, cfg.key, 'DELETE', `/t/${encodeURIComponent(cfg.tournamentId)}/reg/${encodeURIComponent(rid)}`);
}

// --- Vorlagen im Konto (docs/15 §6) ---------------------------------------------

/** Was eine Vorlage aufhebt: alles außer Frist, Datum und Ort — die sind je Turnier neu */
export interface RegTemplateData {
  title?: string;
  note?: string;
  teamSize: number;
  maxTeams?: number;
  fields: RegistrationField[];
}
export interface RegTemplate {
  id: string;
  name: string;
  updatedAt: number;
  data: RegTemplateData;
}

export async function listTemplates(): Promise<RegTemplate[]> {
  if (!DEFAULT_API) return [];
  const r = await apiCall<{ templates: { id: string; updatedAt: number; data: RegTemplateData & { name?: string } }[] }>(DEFAULT_API, undefined, 'GET', '/me/templates');
  return r.templates
    .map((v) => ({
      id: v.id,
      name: v.data.name ?? v.id,
      updatedAt: v.updatedAt,
      data: {
        teamSize: v.data.teamSize ?? 2,
        fields: v.data.fields ?? [],
        ...(v.data.title ? { title: v.data.title } : {}),
        ...(v.data.note ? { note: v.data.note } : {}),
        ...(v.data.maxTeams ? { maxTeams: v.data.maxTeams } : {}),
      },
    }))
    .sort((a, b) => a.name.localeCompare(b.name, 'de'));
}

export async function saveTemplate(name: string, data: RegTemplateData, id = 'v' + newFieldId()): Promise<RegTemplate> {
  const updatedAt = Date.now();
  await apiCall(DEFAULT_API, undefined, 'PUT', `/me/templates/${encodeURIComponent(id)}`, { updatedAt, data: { ...data, name } });
  return { id, name, updatedAt, data };
}

export async function deleteTemplate(id: string): Promise<void> {
  await apiCall(DEFAULT_API, undefined, 'DELETE', `/me/templates/${encodeURIComponent(id)}`);
}

/** Vorlage aus einer bestehenden Konfiguration bzw. dem Formular */
export function templateFrom(body: Pick<RegistrationBody, 'title' | 'note' | 'teamSize' | 'maxTeams' | 'fields'>): RegTemplateData {
  const d: RegTemplateData = { teamSize: body.teamSize, fields: (body.fields ?? []).map((f) => ({ ...f })) };
  if (body.title) d.title = body.title;
  if (body.note) d.note = body.note;
  if (body.maxTeams) d.maxTeams = body.maxTeams;
  return d;
}

/** Öffentlicher Link: #/anmelden?t=<id>&r=<regKey>[&api=…] */
export function registrationLink(cfg: Pick<SyncConfig, 'tournamentId' | 'apiUrl'>, regKey: string): string {
  const base = `${location.origin}${location.pathname}`;
  const params = new URLSearchParams({ t: cfg.tournamentId, r: regKey });
  if (cfg.apiUrl !== DEFAULT_API) params.set('api', cfg.apiUrl);
  return `${base}#/anmelden?${params.toString()}`;
}

/**
 * Standard-Anmeldeschluss: Tag vor dem Turnier, 23:59 Ortszeit. Liegt das schon
 * hinter uns (Turnier ist heute), dann der Turniertag 23:59; sonst in einer Woche.
 */
export function defaultDeadline(dateISO: string | undefined, now = Date.now()): number {
  const week = now + 7 * 24 * 3600 * 1000;
  if (!dateISO) return week;
  const day = new Date(`${dateISO}T23:59:00`);
  if (Number.isNaN(day.getTime())) return week;
  const before = new Date(day);
  before.setDate(before.getDate() - 1);
  for (const c of [before, day]) if (c.getTime() > now) return c.getTime();
  return week;
}

/** Unix-ms ↔ Wert für <input type="datetime-local"> (Ortszeit) */
export function toLocalInput(ms: number): string {
  const d = new Date(ms);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}
export function fromLocalInput(s: string): number {
  const t = new Date(s).getTime();
  return Number.isNaN(t) ? 0 : t;
}

// --- Teilnehmer (öffentlich, ohne Key) ------------------------------------------

export async function registrationInfo(apiUrl: string, tournamentId: string, regKey: string): Promise<RegistrationInfo> {
  return apiCall(apiUrl, undefined, 'GET', `/t/${encodeURIComponent(tournamentId)}/anmeldung/${encodeURIComponent(regKey)}`);
}

export async function register(apiUrl: string, tournamentId: string, regKey: string, body: RegistrationInput): Promise<{ id: string; token: string; position: number; waitlist: boolean }> {
  return apiCall(apiUrl, undefined, 'POST', `/t/${encodeURIComponent(tournamentId)}/anmeldung/${encodeURIComponent(regKey)}`, body);
}

export async function withdraw(apiUrl: string, tournamentId: string, regKey: string, rid: string, token: string): Promise<{ deleted: boolean }> {
  return apiCall(apiUrl, undefined, 'DELETE', `/t/${encodeURIComponent(tournamentId)}/anmeldung/${encodeURIComponent(regKey)}/${encodeURIComponent(rid)}?token=${encodeURIComponent(token)}`);
}

/** Eigene Anmeldung(en) auf diesem Gerät merken — für „zurückziehen“ */
export interface OwnRegistration {
  id: string;
  token: string;
  label: string;
}
const ownKey = (tournamentId: string) => `airmail.reg.${tournamentId}`;
export function loadOwn(tournamentId: string): OwnRegistration[] {
  try {
    return JSON.parse(localStorage.getItem(ownKey(tournamentId)) ?? '[]') as OwnRegistration[];
  } catch {
    return [];
  }
}
export function saveOwn(tournamentId: string, list: OwnRegistration[]): void {
  try {
    localStorage.setItem(ownKey(tournamentId), JSON.stringify(list));
  } catch {
    /* privater Modus o. Ä. */
  }
}
